import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { Check, Flag } from 'lucide-react';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatDays, formatMinutes, formatNumber, formatShortTime, formatWeekday } from '../lib/format';
import { Card, StatusDot, TimeRange } from './ui';
import { CARD_COLOURS } from './WorkProgress';

type TargetState = 'MET' | 'IN_PROGRESS' | 'SHORT';

/** The Team board's status of a work date. */
type DayStatus =
  | 'WORKING'
  | 'ON_BREAK'
  | 'PAUSED'
  | 'ENDED'
  | 'ON_LEAVE'
  | 'OFF'
  | 'UPCOMING'
  | 'NOT_STARTED'
  | 'ABSENT'
  | 'NO_SCHEDULE';

/** `hours` on `GET /dashboard`: the reader's own day against the hours it owes (domain/hours-progress.ts). */
export interface WorkHoursBlock {
  /** The day being worked, YYYY-MM-DD. */
  date: string;
  serverNow: string;
  status: DayStatus;
  /** Worked time is going up right now: the card counts on between reads. */
  ticking: boolean;
  workedMinutes: number;
  breakMinutes: number;
  /** Null on a day that owes nothing: a day off, leave, a holiday, no roster. */
  target: {
    requiredMinutes: number;
    state: TargetState;
    missingMinutes: number;
    extraMinutes: number;
    source: 'SHIFT' | 'DEFAULT' | string;
    /** As judged once the day was over. */
    decided: boolean;
  } | null;
  finishAt: string | null;
  shift: { startAt: string | null; endAt: string | null; name: string | null; nameAr: string | null } | null;
  /** What a day that ends short costs, in days of salary; 0: nothing. */
  shortDayPenaltyDays: number;
  /** A day that meets its hours drops its break and inactivity penalties. */
  waivesPenalties: boolean;
  /** The day's Regular Shift is over and is not started again (CR-006): its worked time is final. Missing from an older API. */
  regularOver?: boolean;
  /** The day's Free Shifts — tracked, unpaid, never in `workedMinutes` — and Total tracked. Missing from an older API. */
  free?: { count: number; workedMinutes: number; running: boolean; totalTrackedMinutes: number };
  /** The seven days, oldest first, the day being worked the last. */
  week: Array<{ date: string; workedMinutes: number; requiredMinutes: number | null; state: TargetState | null }>;
}

/**
 * Where the day stands, in words: nothing owed; the shift not begun, or begun
 * and not started; the clock running — under way, past half, nearly; stopped —
 * on a break, or away from the computer; met; ended short; never came.
 */
type Stage = 'none' | 'before' | 'start' | 'going' | 'half' | 'almost' | 'paused' | 'away' | 'met' | 'short' | 'absent';

const STAGE_TITLES: Record<Exclude<Stage, 'short'>, TranslationKey> = {
  none: 'dash.hours.none',
  before: 'dash.hours.before',
  start: 'dash.hours.start',
  going: 'dash.hours.going',
  half: 'dash.hours.half',
  almost: 'dash.hours.almost',
  paused: 'dash.hours.paused',
  away: 'dash.hours.away',
  met: 'dash.hours.met',
  absent: 'dash.hours.absent',
};

function stageOf(block: WorkHoursBlock, worked: number): Stage {
  const target = block.target;
  if (!target) return 'none';
  if (target.state === 'MET' || (target.state === 'IN_PROGRESS' && worked >= target.requiredMinutes)) return 'met';
  if (target.state === 'SHORT') return block.status === 'ABSENT' ? 'absent' : 'short';
  if (block.status === 'UPCOMING') return 'before';
  if (block.status === 'NOT_STARTED') return 'start';
  if (!block.ticking) return block.status === 'WORKING' ? 'away' : 'paused';
  const share = worked / target.requiredMinutes;
  return share >= 0.85 ? 'almost' : share >= 0.5 ? 'half' : 'going';
}

/**
 * The minutes worked now: the read's, and while the clock runs, a minute more
 * for every minute since it arrived — counted by this browser's clock from when
 * the read came, so a clock set wrong on the computer moves nothing.
 */
function useLiveMinutes(block: WorkHoursBlock): number {
  const arrived = useMemo(() => Date.now(), [block]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!block.ticking) return;
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, [block]);
  return block.ticking ? block.workedMinutes + Math.max(0, Math.floor((now - arrived) / 60_000)) : block.workedMinutes;
}

/** "6:30" — a day's hours in the week's narrow columns, in the machine voice. */
const clock = (minutes: number) => `${Math.floor(minutes / 60)}:${String(Math.max(0, minutes) % 60).padStart(2, '0')}`;

/**
 * The reader's own hours against what their day owes, under the progress card
 * (operator, 2026-11-02: "this progress for tasks and it's very good i need now
 * for my target working hours another creative one in web and app"). A track of
 * hour cells, one for each hour the day owes, fills green as they work, its
 * leading edge pulsing while the clock runs; met, the flag turns an amber check
 * and the extra hours past it are amber (the cards' colours: CARD_COLOURS). Beside it, where the day stands in words, what is left and
 * when it will be done, and the last seven days, each a gauge against its own
 * target. The figures are the Team board's (the day's shift's Standard hours,
 * else the default; worked less rest breaks, pauses and Not working), so the
 * card, the board and the short-day rule agree.
 */
export function WorkHoursCard({ block }: { block: WorkHoursBlock }) {
  const { t, locale, localized } = useI18n();
  const worked = useLiveMinutes(block);
  const stage = stageOf(block, worked);
  const target = block.target;
  const required = target?.requiredMinutes ?? 0;
  const missing = Math.max(0, required - worked);
  const extra = Math.max(0, worked - required);
  const m = (minutes: number) => formatMinutes(minutes, locale);
  const shine = useShine(stage === 'met', block.date);

  const hint = (() => {
    const at = block.shift?.startAt ? formatShortTime(block.shift.startAt, locale) : '—';
    const until = formatShortTime(new Date(Date.now() + missing * 60_000), locale);
    switch (stage) {
      case 'none':
        if (block.status === 'OFF') return t('dash.hours.noneOff');
        if (block.status === 'ON_LEAVE') return t('dash.hours.noneLeave');
        return worked > 0 ? t('dash.hours.noneWorked', { worked: m(worked) }) : t('dash.hours.noneHint');
      case 'before':
        return t('dash.hours.beforeHint', { required: m(required) });
      case 'start':
        return t('dash.hours.startHint', { time: at });
      case 'going':
      case 'half':
      case 'almost':
        return t('dash.hours.goingHint', { missing: m(missing), time: until });
      case 'paused':
        return t('dash.hours.pausedHint', { missing: m(missing) });
      case 'away':
        return t('dash.hours.awayHint');
      case 'met':
        return [
          extra > 0 ? t('dash.hours.metExtra', { extra: m(extra), required: m(required) }) : t('dash.hours.metHint', { required: m(required) }),
          block.waivesPenalties ? t('dash.hours.metWaived') : null,
        ]
          .filter(Boolean)
          .join(' ');
      case 'short':
        return [
          t('dash.hours.shortHint', { required: m(required) }),
          block.shortDayPenaltyDays > 0 && !target?.decided
            ? t('dash.hours.shortCost', { days: formatDays(block.shortDayPenaltyDays, locale) })
            : null,
          // It is started once a work date (CR-006): nothing makes the hours up — a Free Shift is apart.
          target?.decided ? null : t('dash.hours.shortFree'),
        ]
          .filter(Boolean)
          .join(' ');
      case 'absent':
        return t('dash.hours.absentHint');
    }
  })();

  const title = stage === 'short' ? t('dash.hours.short', { missing: m(missing) }) : t(STAGE_TITLES[stage]);
  const dot: { tone: 'online' | 'idle' | 'offline' | 'alert'; pulse: boolean; label: string } =
    block.status === 'WORKING' && !block.ticking
      ? { tone: 'alert', pulse: false, label: t('presence.NOT_WORKING') }
      : block.status === 'WORKING'
        ? { tone: 'online', pulse: true, label: t('attendance.status.WORKING') }
        : block.status === 'ON_BREAK'
          ? { tone: 'idle', pulse: false, label: t('attendance.status.ON_BREAK') }
          : block.status === 'PAUSED'
            ? { tone: 'idle', pulse: false, label: t('attendance.status.PAUSED') }
            : { tone: 'offline', pulse: false, label: t(`attendance.status.${block.status}` as TranslationKey) };

  return (
    <section className="mb-8">
      <Card padded={false} className={clsx('relative overflow-hidden', stage === 'met' && 'border-warning/50 dark:shadow-edge-signal')}>
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
          <div className="min-w-0 p-5">
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
              <div className="min-w-0">
                <h2 className="font-sans text-overline uppercase text-[var(--text-tertiary)] rtl:font-arabic">{t('dash.hours.title')}</h2>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 font-mono text-mono-sm tabular text-faint">
                  {/* Noon, so the day reads the same in every time zone. */}
                  <span>
                    {formatWeekday(block.date, locale, 'long')} {formatDate(`${block.date}T12:00:00`, locale)}
                  </span>
                  {block.shift?.startAt && (
                    <>
                      <span aria-hidden>·</span>
                      {block.shift.name && <span className="font-sans">{localized({ name: block.shift.name, nameAr: block.shift.nameAr })}</span>}
                      <TimeRange from={block.shift.startAt} to={block.shift.endAt} />
                    </>
                  )}
                </p>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-pill bg-neutral-500/12 px-2.5 py-0.5 text-caption text-[var(--text-secondary)] ring-1 ring-inset ring-neutral-500/25">
                <StatusDot tone={dot.tone} pulse={dot.pulse} />
                {dot.label}
              </span>
            </div>

            <p className="mt-4 flex flex-wrap items-baseline gap-x-2">
              <span className="text-metric tabular text-[var(--text-primary)]">{m(worked)}</span>
              {target && <span className="text-body text-muted">{t('dash.hours.of', { required: m(required) })}</span>}
            </p>
            <p className="mt-1 font-display text-title text-[var(--text-primary)] rtl:font-arabic">{title}</p>
            <p className="text-body-sm text-muted">{hint}</p>

            {target ? (
              <HoursTrack worked={worked} required={required} ticking={block.ticking} met={stage === 'met'} shine={shine} />
            ) : (
              <p className="mt-4 rounded-md border border-dashed hairline px-3 py-2.5 text-body-sm text-muted">
                {t('dash.hours.noneTrack')}
              </p>
            )}
            {target && (
              <p className="mt-2 text-caption text-faint">
                {target.decided
                  ? t('attendance.targetJudged')
                  : target.source === 'SHIFT'
                    ? t('dash.hours.fromShift')
                    : t('dash.hours.fromDefault')}
                {block.breakMinutes > 0 && ` · ${t('dash.hours.breaks', { minutes: m(block.breakMinutes) })}`}
                {' · '}
                <Link to="/attendance" className="font-medium text-primary-600 hover:underline dark:text-primary-400">
                  {t('attendance.myShift')}
                </Link>
              </p>
            )}
            {/* The day's Free Shifts (CR-006): said apart, never on the track — they make up none of its hours. */}
            {block.free && (block.free.count > 0 || block.free.running) && (
              <p className="mt-1 text-caption text-muted">
                {block.free.running && <span className="text-[var(--text-secondary)]">{t('dash.hours.freeRunning')} </span>}
                <span className="tabular">
                  {t('dash.hours.freeLine', { minutes: m(block.free.workedMinutes), total: m(block.free.totalTrackedMinutes) })}
                </span>
              </p>
            )}
          </div>

          <div className="min-w-0 border-t hairline p-5 xl:border-s xl:border-t-0">
            <HoursWeek week={block.week} today={block.date} />
          </div>
        </div>
      </Card>
    </section>
  );
}

/** Extra hours drawn past the flag before the rest is only a figure. */
const EXTRA_CELLS = 3;

/**
 * The day's hours as cells: one an hour, the last the part of an hour a target
 * like 7h 30m leaves; each fills as it is worked, the one filling with a bright
 * edge that pulses while the clock runs. Past the flag, the extra hours. Runs
 * from the start of the reading: right to left in Arabic.
 */
function HoursTrack({
  worked,
  required,
  ticking,
  met,
  shine,
}: {
  worked: number;
  required: number;
  ticking: boolean;
  met: boolean;
  shine: boolean;
}) {
  const { t, locale } = useI18n();
  const cells = Array.from({ length: Math.ceil(required / 60) }, (_, i) => ({ from: i * 60, minutes: Math.min(60, required - i * 60) }));
  const extra = Math.max(0, worked - required);
  const extraCells = Math.min(EXTRA_CELLS, Math.ceil(extra / 60));
  const m = (minutes: number) => formatMinutes(minutes, locale);

  return (
    <div className="mt-4">
      <div
        className="relative flex items-center gap-2"
        role="progressbar"
        aria-label={t('dash.hours.title')}
        aria-valuemin={0}
        aria-valuemax={required}
        aria-valuenow={Math.min(worked, required)}
        aria-valuetext={t('dash.hours.label', { worked: m(worked), required: m(required) })}
      >
        <div className="relative flex min-w-0 flex-1 gap-1">
          {cells.map((cell, i) => {
            const fill = Math.min(1, Math.max(0, (worked - cell.from) / cell.minutes));
            const leading = fill > 0 && fill < 1;
            return (
              <span
                key={i}
                className="relative h-8 min-w-0 basis-0 overflow-hidden rounded-sm bg-[var(--surface-sunken)] ring-1 ring-inset ring-[var(--line)]"
                style={{ flexGrow: cell.minutes }}
              >
                <span
                  className={clsx('absolute inset-y-0 start-0 transition-[width] duration-slow ease-precision', CARD_COLOURS.done)}
                  style={{ width: `${fill * 100}%` }}
                />
                {/* The minute being worked: a bright edge that pulses while the clock runs. */}
                {leading && (
                  <span
                    className={clsx('absolute inset-y-0 w-0.5 bg-white/90', ticking && 'motion-safe:animate-pulse-signal')}
                    style={{ insetInlineStart: `calc(${fill * 100}% - 1px)` }}
                    aria-hidden
                  />
                )}
              </span>
            );
          })}
          {/* Met: a light runs along the track once. */}
          {shine && (
            <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-sm flip-rtl" aria-hidden>
              <span className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/60 to-transparent opacity-0 motion-safe:animate-[hours-shine_1200ms_cubic-bezier(0.2,0,0,1)_both]" />
            </span>
          )}
        </div>

        <span
          className={clsx(
            'grid h-8 w-8 shrink-0 place-items-center rounded-full',
            met ? clsx(CARD_COLOURS.met, 'text-ink') : 'bg-neutral-500/12 text-[var(--text-secondary)]',
          )}
          title={t('dash.hours.target', { required: m(required) })}
          aria-hidden
        >
          {met ? <Check className="h-4 w-4" strokeWidth={3} /> : <Flag className="h-4 w-4 flip-rtl" />}
        </span>

        {extraCells > 0 && (
          <span className="flex shrink-0 gap-1" aria-hidden>
            {Array.from({ length: extraCells }, (_, i) => (
              <span key={i} className="relative h-8 w-6 overflow-hidden rounded-sm bg-[var(--surface-sunken)] ring-1 ring-inset ring-warning/40 sm:w-8">
                <span
                  className={clsx('absolute inset-y-0 start-0', CARD_COLOURS.met)}
                  style={{ width: `${Math.min(1, Math.max(0, (extra - i * 60) / 60)) * 100}%` }}
                />
              </span>
            ))}
          </span>
        )}
      </div>

      {/* Under each cell the hour it ends on — every other one past twelve; under the flag, the target; past it, the extra. */}
      <div className="mt-1 flex items-start gap-2 font-mono text-mono-sm tabular text-faint" aria-hidden>
        <div className="flex min-w-0 flex-1 gap-1">
          {cells.map((cell, i) => (
            <span key={i} className="min-w-0 basis-0 truncate text-end" style={{ flexGrow: cell.minutes }}>
              {i < cells.length - 1 && (cells.length <= 12 || (i + 1) % 2 === 0) ? formatNumber(i + 1, locale) : ''}
            </span>
          ))}
        </div>
        <span className="min-w-[2rem] shrink-0 whitespace-nowrap text-center text-[var(--text-secondary)]">{m(required)}</span>
        {extraCells > 0 && <span className="shrink-0 whitespace-nowrap text-warning-fg dark:text-warning-dark">+{m(extra)}</span>}
      </div>
    </div>
  );
}

/** The browser's note of the last day it saw the day's hours met. */
const SHINE_KEY = 'viganium.hours.celebrated';

/**
 * Whether to run the light along the track now: when the hours come to be met
 * while the card is open, or the first time this browser sees them met that day.
 */
function useShine(met: boolean, day: string): boolean {
  const [shine, setShine] = useState(false);
  const was = useRef<boolean | null>(null);

  useEffect(() => {
    const before = was.current;
    was.current = met;
    if (!met) return;
    let seen = false;
    try {
      seen = window.localStorage.getItem(SHINE_KEY) === day;
      window.localStorage.setItem(SHINE_KEY, day);
    } catch {
      // Storage refused (a private window): shine as if for the first time.
    }
    if (!seen || before === false) setShine(true);
  }, [met, day]);

  useEffect(() => {
    if (!shine) return;
    const timer = window.setTimeout(() => setShine(false), 1600);
    return () => window.clearTimeout(timer);
  }, [shine]);

  return shine;
}

/** Height, in px, of a day's gauge: its whole target. */
const GAUGE = 48;

/**
 * The last seven days, each a gauge as tall as the hours it owed, filled to what
 * was worked — met amber with a tick, short red, today green, a day that owed
 * nothing an outline. Oldest first: on the right in Arabic.
 */
function HoursWeek({ week, today }: { week: WorkHoursBlock['week']; today: string }) {
  const { t, locale } = useI18n();
  const worked = week.reduce((sum, day) => sum + day.workedMinutes, 0);
  const owed = week.filter((day) => day.requiredMinutes != null && day.state !== 'IN_PROGRESS');
  const met = owed.filter((day) => day.state === 'MET').length;

  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-overline uppercase text-[var(--text-tertiary)]">{t('dash.progress.week')}</h3>
        <span className="font-mono text-mono-sm tabular text-muted">
          {owed.length > 0
            ? t('dash.hours.weekTotal', { worked: formatMinutes(worked, locale), met: formatNumber(met, locale), owed: formatNumber(owed.length, locale) })
            : formatMinutes(worked, locale)}
        </span>
      </div>
      <ol className="mt-3 grid grid-cols-7 gap-1.5">
        {week.map((day) => {
          const isToday = day.date === today;
          const share = day.requiredMinutes ? Math.min(1, day.workedMinutes / day.requiredMinutes) : 0;
          const said = day.requiredMinutes
            ? t('dash.hours.dayLabel', {
                day: isToday ? t('dash.progress.today') : `${formatWeekday(day.date, locale, 'long')} ${formatDate(`${day.date}T12:00:00`, locale)}`,
                worked: formatMinutes(day.workedMinutes, locale),
                required: formatMinutes(day.requiredMinutes, locale),
              })
            : t('dash.hours.dayNotOwed', {
                day: isToday ? t('dash.progress.today') : `${formatWeekday(day.date, locale, 'long')} ${formatDate(`${day.date}T12:00:00`, locale)}`,
                worked: formatMinutes(day.workedMinutes, locale),
              });
          return (
            <li key={day.date} title={said} className="flex min-w-0 flex-col items-center gap-1">
              <span className={clsx('font-mono text-mono-sm tabular', day.workedMinutes > 0 ? 'text-muted' : 'text-faint')} aria-hidden>
                {day.workedMinutes > 0 ? clock(day.workedMinutes) : '–'}
              </span>
              <span
                className={clsx(
                  'relative w-full max-w-[1.75rem] overflow-hidden rounded-sm',
                  day.requiredMinutes ? 'bg-[var(--surface-sunken)] ring-1 ring-inset' : 'border border-dashed',
                  day.state === 'SHORT' ? 'ring-error/50' : day.state === 'MET' ? 'ring-warning/50' : 'ring-[var(--line)] hairline',
                )}
                style={{ height: GAUGE }}
                aria-hidden
              >
                {share > 0 && (
                  <span
                    className={clsx(
                      'absolute inset-x-0 bottom-0 transition-[height] duration-slow ease-precision',
                      day.state === 'MET'
                        ? CARD_COLOURS.met
                        : isToday
                          ? CARD_COLOURS.done
                          : day.state === 'SHORT'
                            ? CARD_COLOURS.late
                            : CARD_COLOURS.past,
                    )}
                    style={{ height: `${share * 100}%` }}
                  />
                )}
              </span>
              <span
                className={clsx(
                  'flex max-w-full items-center gap-0.5 truncate text-caption',
                  isToday ? 'font-semibold text-success-fg dark:text-success' : 'text-[var(--text-tertiary)]',
                )}
                aria-hidden
              >
                {day.state === 'MET' && <Check className="h-3 w-3 shrink-0 text-warning-fg dark:text-warning-dark" strokeWidth={3} />}
                {formatWeekday(day.date, locale, locale === 'ar' ? 'narrow' : 'short')}
              </span>
              <span className="sr-only">{said}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
